import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Eye, RotateCcw, SwatchBook, X } from 'lucide-react';
import { MARD_291, hexToRgb, readableTextColor } from '../core/palette';
import type { ProcessResult } from '../types';

interface Props {
  result: ProcessResult;
  title?: string;
  onCancel: () => void;
  onConfirm: (result: ProcessResult) => void;
}

function colorDistance(left: string, right: string) {
  const a = hexToRgb(left);
  const b = hexToRgb(right);
  const redMean = (a.r + b.r) / 2;
  const red = a.r - b.r;
  const green = a.g - b.g;
  const blue = a.b - b.b;
  return Math.sqrt((2 + redMean / 256) * red * red + 4 * green * green + (2 + (255 - redMean) / 256) * blue * blue);
}

function ResultPreview({ result, replacements }: { result: ProcessResult; replacements: Map<number, number> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = result.width;
    canvas.height = result.height;
    const context = canvas.getContext('2d');
    if (!context) return;
    const image = context.createImageData(result.width, result.height);
    for (let index = 0; index < result.colorIndices.length; index += 1) {
      const pixel = index * 4;
      if (result.empty[index]) {
        image.data[pixel + 3] = 0;
        continue;
      }
      const sourceIndex = result.colorIndices[index];
      const targetIndex = replacements.get(sourceIndex) ?? sourceIndex;
      const color = hexToRgb(MARD_291[targetIndex]?.hex || '#ffffff');
      image.data[pixel] = color.r;
      image.data[pixel + 1] = color.g;
      image.data[pixel + 2] = color.b;
      image.data[pixel + 3] = 255;
    }
    context.putImageData(image, 0, 0);
  }, [replacements, result]);
  return <canvas ref={canvasRef} aria-label="近似色校准结果预览" />;
}

export function ColorCalibrationModal({ result, title = '近似色肉眼校准', onCancel, onConfirm }: Props) {
  const counts = useMemo(() => {
    const values = new Map<number, number>();
    for (let index = 0; index < result.colorIndices.length; index += 1) {
      if (!result.empty[index]) values.set(result.colorIndices[index], (values.get(result.colorIndices[index]) || 0) + 1);
    }
    return [...values.entries()].sort((left, right) => right[1] - left[1]);
  }, [result]);
  const [selected, setSelected] = useState(counts[0]?.[0] ?? 0);
  const [replacements, setReplacements] = useState<Map<number, number>>(new Map());
  const source = MARD_291[selected];
  const currentTarget = replacements.get(selected) ?? selected;
  const closeColors = useMemo(() => {
    if (!source) return [];
    return MARD_291.map((color, index) => ({ color, index, distance: colorDistance(source.hex, color.hex) }))
      .sort((left, right) => left.distance - right.distance)
      .slice(0, 15);
  }, [source]);

  function finish() {
    const colorIndices = result.colorIndices.slice();
    for (let index = 0; index < colorIndices.length; index += 1) {
      const replacement = replacements.get(colorIndices[index]);
      if (replacement !== undefined) colorIndices[index] = replacement;
    }
    onConfirm({ ...result, colorIndices });
  }

  return <div className="modal-backdrop color-calibration-backdrop" role="dialog" aria-modal="true" aria-labelledby="color-calibration-title">
    <div className="color-calibration-modal">
      <button className="icon-button modal-close" type="button" onClick={onCancel} aria-label="关闭近似色校准"><X size={18} /></button>
      <div className="color-calibration-heading">
        <div><p className="eyebrow">MARD 291 · 人工复核</p><h2 id="color-calibration-title"><SwatchBook size={22} />{title}</h2></div>
        <span><Eye size={16} />先看原色，再点最接近实物的色块</span>
      </div>
      <div className="color-calibration-layout">
        <section className="color-calibration-preview">
          <div className="color-calibration-canvas"><ResultPreview result={result} replacements={replacements} /></div>
          <div><strong>{result.width} × {result.height}</strong><span>{counts.length} 个在用颜色 · 已调整 {replacements.size} 个</span></div>
          <p>这里只替换色号，不改变图纸格数、空白区域或构图。</p>
        </section>
        <section className="color-calibration-panel">
          <div className="color-source-list">
            <div><strong>图纸在用颜色</strong><span>选择一个颜色查看接近色</span></div>
            <div>
              {counts.map(([paletteIndex, count]) => {
                const color = MARD_291[paletteIndex];
                const targetIndex = replacements.get(paletteIndex) ?? paletteIndex;
                return <button className={selected === paletteIndex ? 'active' : ''} type="button" key={paletteIndex} onClick={() => setSelected(paletteIndex)}>
                  <i style={{ background: color.hex }} /><span><strong>{color.code}</strong><small>{count} 颗{targetIndex !== paletteIndex ? ` → ${MARD_291[targetIndex].code}` : ''}</small></span>
                </button>;
              })}
            </div>
          </div>
          <div className="near-color-review">
            <div className="near-color-title">
              <span className="near-color-source" style={{ background: source?.hex, color: readableTextColor(source?.hex || '#fff') }}>{source?.code}</span>
              <span><strong>与 {source?.code} 接近的色块</strong><small>按色差由近到远排列，点选后左侧预览立即更新。</small></span>
            </div>
            <div className="near-color-grid">
              {closeColors.map(({ color, index }) => <button className={currentTarget === index ? 'active' : ''} type="button" key={color.code} onClick={() => setReplacements((current) => {
                const next = new Map(current);
                if (index === selected) next.delete(selected);
                else next.set(selected, index);
                return next;
              })}>
                <i style={{ background: color.hex }} />
                <span><strong>{color.code}</strong><small>{index === selected ? '原始匹配' : '接近色'}</small></span>
                {currentTarget === index && <Check size={15} />}
              </button>)}
            </div>
          </div>
        </section>
      </div>
      <div className="color-calibration-actions">
        <button className="secondary-button" type="button" disabled={!replacements.size} onClick={() => setReplacements(new Map())}><RotateCcw size={16} />清除全部调整</button>
        <span>不确定时保留原始匹配即可，之后仍可在编辑模式换色。</span>
        <button className="primary-button" type="button" onClick={finish}><Check size={18} />确认颜色并生成图纸</button>
      </div>
    </div>
  </div>;
}
